import { useCallback, useEffect, useState } from 'react';
import Button from '../Button';
import Alert from '../Alert';
import ClassRowEditor from './ClassRowEditor';
import TransferMembersModal from './TransferMembersModal';
import EnableClassesModal from './EnableClassesModal';
import DisableClassesModal from './DisableClassesModal';
import {
  getEnterpriseClasses, createEnterpriseClass, disableEnterpriseClass,
  reactivateEnterpriseClass,
} from '../../api/admin';

// class-stream 超管班级管理（仅 superadmin 渲染）。
// 回调拆分：本区改动（新建/改名/转班/停用）只刷新本区列表，不关弹窗；
// 仅企业形态变更（启用/关闭分流，classes_enabled 翻转）走 onEnterpriseChanged。
export default function AdminEnterpriseClassesSection({ enterprise, role, onEnterpriseChanged }) {
  const entId = enterprise?.id;
  const classesEnabled = !!enterprise?.classes_enabled;
  const isSuper = role === 'superadmin';

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const [transferFor, setTransferFor] = useState(null);
  const [showEnable, setShowEnable] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!isSuper || !entId || !classesEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await getEnterpriseClasses(entId);
      setClasses(data);
    } catch {
      setError('加载班级列表失败');
    } finally {
      setLoading(false);
    }
  }, [isSuper, entId, classesEnabled]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  if (!isSuper) return null;

  const active = classes.filter((c) => c.is_active);
  const archived = classes.filter((c) => !c.is_active);

  const handleCreate = async () => {
    const name = window.prompt('新建班级 — 请输入班级名称');
    if (name == null) return;
    if (!name.trim()) { setError('班级名称不能为空'); return; }
    setCreating(true); setError('');
    try {
      await createEnterpriseClass(entId, { name: name.trim() });
      loadClasses();
    } catch (e) {
      setError(e.response?.data?.detail || '新建班级失败');
    } finally {
      setCreating(false);
    }
  };

  const handleReactivate = async (cls) => {
    setError('');
    try {
      await reactivateEnterpriseClass(entId, cls.id);
      loadClasses();
    } catch (e) {
      setError(e.response?.data?.detail || '重新启用失败');
    }
  };

  const handleDisable = async (cls) => {
    setError('');
    try {
      await disableEnterpriseClass(entId, cls.id);
      loadClasses();
    } catch (e) {
      const code = e.response?.data?.error_code;
      const map = {
        members_not_empty: '请先转走全部成员后再停用该班级。',
        last_active_class: '不能停用最后一个活跃班级；如需清空请使用「关闭班级分流」。',
      };
      setError(map[code] || e.response?.data?.detail || '停用失败');
    }
  };

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>班级分流（仅超管）</h4>
      {error && <Alert type="error">{error}</Alert>}

      {!classesEnabled ? (
        <div>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
            该企业当前为<strong>裸码</strong>形态：员工注册后直接绑定企业，看板为全员聚合。
            启用班级分流后可按班级分流注册、分班级查看看板。
          </p>
          <Button size="sm" onClick={() => setShowEnable(true)}>启用班级分流</Button>
        </div>
      ) : loading ? (
        <p style={{ color: '#94a3b8', fontSize: 13 }}>加载中...</p>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <Button size="sm" onClick={handleCreate} loading={creating}>新建班级</Button>
            <Button variant="secondary" size="sm" onClick={() => setShowDisable(true)}>关闭班级分流</Button>
          </div>

          <div style={groupLabel}>活跃班级（{active.length}）</div>
          {active.length === 0 ? (
            <p style={emptyHint}>暂无活跃班级。</p>
          ) : (
            <div style={listStyle}>
              {active.map((c) => (
                <ClassRowEditor
                  key={c.id}
                  entId={entId}
                  cls={c}
                  isLastActive={active.length === 1}
                  onTransfer={setTransferFor}
                  onDisable={handleDisable}
                  onRenamed={loadClasses}
                  onError={setError}
                />
              ))}
            </div>
          )}

          {archived.length > 0 && (
            <>
              <div style={{ ...groupLabel, marginTop: 16 }}>归档班级（{archived.length}）</div>
              <div style={listStyle}>
                {archived.map((c) => (
                  <div key={c.id} style={archivedRow}>
                    <code style={codeStyle}>{c.registration_code}</code>
                    <span style={{ fontSize: 14, color: '#64748b', flex: 1 }}>{c.name}</span>
                    <span style={metaStyle}>成员 {c.member_count}</span>
                    <span style={archivedBadge}>归档</span>
                    <button onClick={() => handleReactivate(c)} style={reactivateBtn}>重新启用</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {transferFor && (
        <TransferMembersModal
          entId={entId}
          sourceClass={transferFor}
          activeClasses={active}
          onClose={() => setTransferFor(null)}
          onDone={() => { setTransferFor(null); loadClasses(); }}
        />
      )}
      {showEnable && (
        <EnableClassesModal
          entId={entId}
          employeeCount={enterprise?.employee_count ?? 0}
          onClose={() => setShowEnable(false)}
          onDone={() => { setShowEnable(false); onEnterpriseChanged?.(); }}
        />
      )}
      {showDisable && (
        <DisableClassesModal
          entId={entId}
          activeClassCount={active.length}
          onClose={() => setShowDisable(false)}
          onDone={() => { setShowDisable(false); onEnterpriseChanged?.(); }}
        />
      )}
    </div>
  );
}

const groupLabel = { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 };
const listStyle = { display: 'flex', flexDirection: 'column', gap: 8 };
const emptyHint = { fontSize: 12, color: '#94a3b8', margin: 0 };
const archivedRow = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 };
const codeStyle = { fontSize: 11, background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, color: '#475569' };
const metaStyle = { fontSize: 12, color: '#94a3b8' };
const archivedBadge = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#f1f5f9', color: '#94a3b8' };
const reactivateBtn = { padding: '5px 12px', borderRadius: 8, border: '1px solid #16a34a30', background: '#16a34a0f', color: '#16a34a', fontSize: 12, cursor: 'pointer', fontWeight: 500 };
